import {
  DateFilterTypesEnum,
  EntityType,
  PaidFlowTypesEnum,
  PaymentDueAccountVM,
  PaymentService,
} from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import DateHelper from "../../utils/DateHelper";
import { URLS } from "../../constants/constants";

/** Backend PaymentDueAccount property bound as DateFilter.DateField for payable/receivable windows. */
export const PAYMENT_DUE_DATE_FIELD = "DueDate";

/**
 * Generated-client DateFilter query-arg tuple, in PaymentService parameter order:
 * dateFilterDateFilterTypeId, dateFilterClientDate, dateFilterStartDate, dateFilterEndDate, dateFilterDateField.
 *
 * Next-* presets send TypeId + ClientDate + DateField=DueDate and omit Start/End (DateFilterVM.Validate
 * rejects Start/End on non-Custom). All Payments (null / any other type, including TypeId 0) omits
 * every DateFilter key — never send TypeId 0.
 */
export type PaymentDateFilterQueryArgs = readonly [
  dateFilterDateFilterTypeId: DateFilterTypesEnum | undefined,
  dateFilterClientDate: string | undefined,
  dateFilterStartDate: undefined,
  dateFilterEndDate: undefined,
  dateFilterDateField: typeof PAYMENT_DUE_DATE_FIELD | undefined,
];

export function buildPaymentDateFilterQueryArgs(
  dateFilterType: number | null,
): PaymentDateFilterQueryArgs {
  const isNextRange =
    dateFilterType === DateFilterTypesEnum.Next30Days ||
    dateFilterType === DateFilterTypesEnum.Next3Months ||
    dateFilterType === DateFilterTypesEnum.Next6Months;

  if (!isNextRange) {
    return [undefined, undefined, undefined, undefined, undefined];
  }

  return [
    dateFilterType as DateFilterTypesEnum,
    DateHelper.getClientDate(),
    undefined,
    undefined,
    PAYMENT_DUE_DATE_FIELD,
  ];
}

export type PaymentListingMode = "Payables" | "Receivables";

/**
 * Entity-aware return path for payment listing tabs (payables vs receivables).
 *
 * Only Property and Unit have a scoped details page to return to (`propertydetails/:id` /
 * `unitdetails/:id`). `EntityType.Tenant` deliberately falls through to the global `URLS.PAYMENT`
 * listing -- there is no equivalent per-tenant details route in this app to scope a receivable's
 * listing tab to, unlike property/unit. If one is added, give it an arm here the same way.
 */
export function getPaymentListingPath(options: {
  isEntity?: boolean;
  entityId?: number;
  entityTypeId?: number;
  paidFlowTypeId: PaidFlowTypesEnum;
}): string {
  const { isEntity, entityId, entityTypeId, paidFlowTypeId } = options;
  const paymentMode: PaymentListingMode =
    paidFlowTypeId === PaidFlowTypesEnum.Receiveable ? "Receivables" : "Payables";
  const backParams = `?paymentMode=${paymentMode}&paidFlowTypeId=${paidFlowTypeId}`;
  let backPath: string = URLS.PAYMENT;
  if (isEntity && entityId && entityTypeId) {
    if (entityTypeId === EntityType.Property) {
      backPath = `/properties/propertydetails/${entityId}`;
    } else if (entityTypeId === EntityType.Unit) {
      backPath = `/units/unitdetails/${entityId}`;
    }
  }
  return `${backPath}${backParams}`;
}

export type PayableHeaderFetchKind =
  | { kind: "property"; payeeAccountId: number; entityId: number }
  | { kind: "unit"; payeeAccountId: number; entityId: number }
  | { kind: "tenant"; entityId: number }
  | { kind: "default"; payeeAccountId: string }
  | { kind: "none" };

/** Resolves which payable header API branch EditPayable / PayablesPostPayment should call. */
export function resolvePayableHeaderFetchKind(
  isEntity: boolean,
  entityTypeId: number | undefined,
  entityId: number | undefined,
  payeeAccountId: string | null,
): PayableHeaderFetchKind {
  if (isEntity && entityTypeId === EntityType.Property && payeeAccountId && entityId) {
    return {
      kind: "property",
      payeeAccountId: Number(payeeAccountId),
      entityId: Number(entityId),
    };
  }
  if (isEntity && entityTypeId === EntityType.Unit && payeeAccountId && entityId) {
    return {
      kind: "unit",
      payeeAccountId: Number(payeeAccountId),
      entityId: Number(entityId),
    };
  }
  if (isEntity && entityTypeId === EntityType.Tenant && entityId) {
    return { kind: "tenant", entityId: Number(entityId) };
  }
  if (payeeAccountId) {
    return { kind: "default", payeeAccountId };
  }
  return { kind: "none" };
}

/**
 * True whenever an apiHelper response signals a real failure: an explicit `errors`/`statusCode`,
 * a missing response entirely (apiHelper resolves `undefined` on a 401), or `data` itself being
 * null/undefined -- the shape apiHelper resolves to for a fully unsignaled network/CORS failure
 * (`{ data: null, errors: null, statusCode: null }`), which is otherwise indistinguishable from a
 * deliberately-empty response. Does NOT treat `data.success` being null/empty as failure on its
 * own -- a present `data` with an empty/null `success` is a legitimate "nothing here" response
 * for endpoints that return that shape; callers whose endpoint never legitimately does that (e.g.
 * the header endpoints below, which 404 instead) layer their own check on top.
 */
export function isApiFailure(res: { data?: any; errors?: any; statusCode?: number | null } | null | undefined): boolean {
  return !res || !!res.errors || !!res.statusCode || res.data == null;
}

/**
 * Every payable/receivable header endpoint (PaymentDueAccountService.GetPayableHeaderInfoByPayeeIdAsync,
 * InvoiceService.GetPayableHeaderInfoFor*ByPayeeIdAsync, GetReceivableHeaderInfoForTenantByTenantIdAsync)
 * throws EntityNotFoundException -- an HTTP 404 with an Errors body -- when there is nothing left
 * to pay, which is the *expected*, successful "fully deleted" outcome, not a failure. Interprets
 * an apiHelper response accordingly: a 404, or a present response whose `success` payload is
 * null/undefined, both resolve to `null` (genuinely gone); anything `isApiFailure` flags (a real
 * error, including an unsignaled network failure or a 401) throws, so the caller can tell a real
 * failure apart from "genuinely gone" instead of collapsing all of them into the same falsy
 * result.
 */
export function interpretHeaderResponse(
  res: { data?: any; errors?: any; statusCode?: number | null } | null | undefined,
): PaymentDueAccountVM | null {
  if (res?.statusCode === 404) {
    return null;
  }
  if (isApiFailure(res)) {
    throw new Error("Failed to load payment header");
  }
  return res!.data.success ?? null;
}

/**
 * Fetches the payable header for EditPayable / PayablesPostPayment, dispatching to the
 * property/unit/tenant/default header API per `resolvePayableHeaderFetchKind`.
 *
 * When the resolved kind is "none", most callers have nothing to fetch and expect `null`.
 * PayablesPostPayment is the one exception: it always has a route `id` to fall back to, so
 * it passes `fallbackId` to fetch that specific payment due account instead of returning null.
 */
export async function loadPayableHeader(
  isEntity: boolean,
  entityTypeId: number | undefined,
  entityId: number | undefined,
  payeeAccountId: string | null,
  fallbackId?: string | number,
): Promise<PaymentDueAccountVM | null> {
  const fetchKind = resolvePayableHeaderFetchKind(isEntity, entityTypeId, entityId, payeeAccountId);

  if (fetchKind.kind === "none") {
    if (fallbackId === undefined) {
      return null;
    }
    const res = await apiHelper(
      PaymentService.getApiPaymentDueAccountPayable,
      { showNotification: false },
      fallbackId,
    );
    return interpretHeaderResponse(res);
  }

  let res;
  if (fetchKind.kind === "property") {
    res = await apiHelper(
      PaymentService.getApiInvoicePayeeAccountPropertyHeader,
      { showNotification: false },
      fetchKind.payeeAccountId,
      fetchKind.entityId,
    );
  } else if (fetchKind.kind === "unit") {
    res = await apiHelper(
      PaymentService.getApiInvoicePayeeAccountUnitHeader,
      { showNotification: false },
      fetchKind.payeeAccountId,
      fetchKind.entityId,
    );
  } else if (fetchKind.kind === "tenant") {
    res = await apiHelper(
      PaymentService.getApiInvoicePayeeAccountTenantHeader,
      { showNotification: false },
      fetchKind.entityId,
    );
  } else {
    res = await apiHelper(
      PaymentService.getApiPaymentDueAccountPayable,
      { showNotification: false },
      fetchKind.payeeAccountId,
    );
  }
  return interpretHeaderResponse(res);
}

export type RefreshAfterDeleteOutcome<T> =
  | { action: "navigate" }
  | { action: "select"; payment: T };

/**
 * Pure decision for EditPayable refresh-after-delete: leave listing vs stay and select next due.
 * Callers are expected to have already handled "the header itself is gone" before calling this --
 * it only decides what to do once a header exists but the dues list may or may not be empty.
 */
export function resolveRefreshAfterDeleteOutcome<T extends { id?: number }>(
  dues: T[],
  selectedPayment: T | null,
): RefreshAfterDeleteOutcome<T> {
  if (dues.length === 0) {
    return { action: "navigate" };
  }
  const next =
    (selectedPayment && dues.find((d) => d.id === selectedPayment.id)) || dues[0];
  return { action: "select", payment: next };
}

export const PAYABLE_FILTER = `paidFlowTypeId == ${PaidFlowTypesEnum.Payable}`;
export const RECEIVABLE_FILTER = `paidFlowTypeId == ${PaidFlowTypesEnum.Receiveable}`;

/**
 * Client-side guard: keep only dues matching a paid-flow (payable vs receivable).
 * The backend's invoiceitems-by-reference-number lookup is org-wide (same Invoice/Bill No.
 * can collide across payees and across payable/receivable rows). Callers must:
 * 1) pass FullyQualifiedFilters via buildInvoiceItemsFullyQualifiedFilter, and
 * 2) apply filterDuesByPaidFlow + scopeInvoiceItemsForEdit on the response.
 *
 * `keepUnknown` decides what happens to rows the API returns with no paidFlowTypeId:
 * pass `true` for edit screens (so a blank flow field never blanks the whole form),
 * `false` for filterPayableDues's strict payable-only guard.
 */
export function filterDuesByPaidFlow<T extends { paidFlowTypeId?: number | null }>(
  dues: readonly T[] | null | undefined,
  paidFlowTypeId: PaidFlowTypesEnum,
  options?: { keepUnknown?: boolean },
): T[] {
  if (!dues?.length) return [];
  const keepUnknown = options?.keepUnknown ?? false;
  return dues.filter(
    (d) => d.paidFlowTypeId === paidFlowTypeId || (keepUnknown && d.paidFlowTypeId == null),
  );
}

/** Client-side guard: settle/edit payable screens must never show receivable dues. */
export function filterPayableDues<T extends { paidFlowTypeId?: number | null }>(
  dues: readonly T[] | null | undefined,
): T[] {
  return filterDuesByPaidFlow(dues, PaidFlowTypesEnum.Payable, { keepUnknown: false });
}

/**
 * Dynamic-LINQ filter for GET .../invoiceitems. The reference-number lookup is org-wide
 * and will otherwise return every payee/tenant that reused the same Invoice/Bill No.
 */
export function buildInvoiceItemsFullyQualifiedFilter(options: {
  paidFlowTypeId: PaidFlowTypesEnum;
  payeeAccountId?: number | null;
  entityId?: number | null;
}): string {
  const flow = `paidFlowTypeId == ${options.paidFlowTypeId}`;
  if (options.payeeAccountId != null && options.payeeAccountId > 0) {
    return `payeeAccountId == ${options.payeeAccountId} && ${flow}`;
  }
  if (options.entityId != null && options.entityId > 0) {
    return `entityId == ${options.entityId} && ${flow}`;
  }
  return flow;
}

export type InvoiceItemsEditScope = {
  paymentDueAccountIds?: readonly number[] | null;
  payeeAccountId?: number | null;
  entityId?: number | null;
};

/**
 * After invoiceitems fetch (+ paid-flow filter), keep only lines for this edit target.
 * Prefer paymentDueAccountIds from the list card; else payeeAccountId; else entityId (tenant).
 */
export function scopeInvoiceItemsForEdit<
  T extends { id?: number | null; payeeAccountId?: number | null; entityId?: number | null },
>(dues: readonly T[] | null | undefined, scope: InvoiceItemsEditScope): T[] {
  if (!dues?.length) return [];

  const scopedIds = (scope.paymentDueAccountIds ?? []).filter(
    (id): id is number => typeof id === "number" && id > 0,
  );
  if (scopedIds.length > 0) {
    const idSet = new Set(scopedIds);
    const byIds = dues.filter((d) => d.id != null && idSet.has(d.id));
    // Stale grouped ids must not blank the form — fall through to payee/entity scope.
    if (byIds.length > 0) {
      return byIds;
    }
  }

  if (scope.payeeAccountId != null && scope.payeeAccountId > 0) {
    return dues.filter((d) => d.payeeAccountId === scope.payeeAccountId);
  }

  if (scope.entityId != null && scope.entityId > 0) {
    return dues.filter((d) => d.entityId === scope.entityId);
  }

  return [...dues];
}

